import { useState, useEffect } from "react"
import { Link } from "react-router"
import { useTheme } from "next-themes"
import { ArrowLeft, LogOut, Sun, Moon, Monitor, ChevronRight, ChevronDown, BarChart3, Grid2x2, Users, Home } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { toast } from "sonner"
import { supabase } from "@/lib/supabase"
import { useAuth } from "@/lib/auth-context"
import { validateAndToast, updateProfileSchema } from '@/lib/schemas'
import { useProfiles, useUpdateProfile, useWeeklyViewStyle, useScope } from "@/lib/hooks"

const themeOptions = [
  { value: "light", label: "ライト", icon: Sun },
  { value: "dark", label: "ダーク", icon: Moon },
  { value: "system", label: "自動", icon: Monitor },
] as const

const weeklyViewOptions = [
  { value: "dots", label: "ドット", icon: BarChart3 },
  { value: "heatmap", label: "ヒートマップ", icon: Grid2x2 },
] as const

const scopeOptions = [
  { value: "all", label: "すべて", icon: Users },
  { value: "household", label: "家計", icon: Home },
  { value: "personal", label: "個人", icon: Users },
] as const

const PREVIEW_DAYS = [
  { day: 10, amount: "¥3,200", colors: ["#ef4444", "#3b82f6"], heat: 3 },
  { day: 11, amount: "¥1,500", colors: ["#ef4444"], heat: 1 },
  { day: 12, amount: null, colors: [], heat: 0 },
  { day: 13, amount: "¥5,800", colors: ["#22c55e", "#ef4444", "#a855f7"], heat: 4 },
  { day: 14, amount: "¥900", colors: ["#3b82f6"], heat: 1 },
  { day: 15, amount: "¥8,200", colors: ["#ef4444", "#22c55e"], heat: 4 },
  { day: 16, amount: null, colors: [], heat: 0 },
] as const

const HEAT_PREVIEW: Record<number, string> = {
  0: "bg-muted",
  1: "bg-orange-200 dark:bg-orange-900/60",
  2: "bg-orange-300 dark:bg-orange-700/70",
  3: "bg-orange-400 dark:bg-orange-600/80",
  4: "bg-orange-500 dark:bg-orange-500",
}

const DAY_LABELS_PREVIEW = ["月", "火", "水", "木", "金", "土", "日"] as const

// スパークライン用
const SPARK_AMOUNTS = [3200, 1500, 0, 5800, 900, 8200, 0]
const SPARK_MAX = Math.max(...SPARK_AMOUNTS)

// monotone spline に近い catmull-rom → cubic bezier 変換
function buildSmoothPath(amounts: number[], max: number) {
  const pts = amounts.map((a, i) => ({
    x: (i / (amounts.length - 1)) * 100,
    y: 100 - (max > 0 ? (a / max) * 100 : 0),
  }))

  // cubic bezier through points using catmull-rom (tension=0)
  let d = `M${pts[0].x},${pts[0].y}`
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(i - 1, 0)]
    const p1 = pts[i]
    const p2 = pts[i + 1]
    const p3 = pts[Math.min(i + 2, pts.length - 1)]

    const cp1x = p1.x + (p2.x - p0.x) / 6
    const cp1y = p1.y + (p2.y - p0.y) / 6
    const cp2x = p2.x - (p3.x - p1.x) / 6
    const cp2y = p2.y - (p3.y - p1.y) / 6

    d += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`
  }
  return { linePath: d, areaPath: `${d} L100,100 L0,100 Z` }
}

const { linePath: SPARK_LINE, areaPath: SPARK_AREA } = buildSmoothPath(SPARK_AMOUNTS, SPARK_MAX)

function WeeklyViewPreview({ style }: { style: string }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <p className="mb-2 text-center text-[10px] text-muted-foreground">表示イメージ</p>
      <div className="mb-1.5 grid grid-cols-7 gap-1 text-center text-[10px] text-muted-foreground">
        {DAY_LABELS_PREVIEW.map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="relative">
        {/* Day cells */}
        <div className="grid grid-cols-7 gap-1">
          {PREVIEW_DAYS.map((d) => {
            const hasAmount = !!d.amount
            const hasDots = style === "dots" && d.colors.length > 0
            return (
              <div
                key={d.day}
                className={`flex aspect-square flex-col items-center justify-center gap-0.5 rounded-md ${
                  style === "heatmap" ? HEAT_PREVIEW[d.heat] : "bg-muted"
                }`}
              >
                <span className="text-xs font-medium">{d.day}</span>
                <span className={`text-[10px] leading-tight text-foreground/80${hasAmount ? "" : " invisible"}`}>
                  {d.amount ?? "\u00A0"}
                </span>
                <div className={`flex flex-wrap justify-center gap-0.5${hasDots ? "" : " invisible"}`}>
                  {d.colors.length > 0
                    ? d.colors.map((c) => (
                        <span
                          key={c}
                          className="inline-block size-1.5 rounded-full"
                          style={{ backgroundColor: c }}
                        />
                      ))
                    : <span className="inline-block size-1.5" />}
                </div>
              </div>
            )
          })}
        </div>
        {/* Sparkline overlay (dots variant only) */}
        {style === "dots" && (
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className="pointer-events-none absolute inset-0 size-full"
          >
            <defs>
              <linearGradient id="previewSparkFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="oklch(0.705 0.213 47.604)" stopOpacity={0.2} />
                <stop offset="100%" stopColor="oklch(0.705 0.213 47.604)" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <path d={SPARK_AREA} fill="url(#previewSparkFill)" />
            <path
              d={SPARK_LINE}
              fill="none"
              stroke="oklch(0.705 0.213 47.604)"
              strokeWidth="2"
              strokeOpacity="0.5"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        )}
      </div>
    </div>
  )
}

export default function AccountPage() {
  const { user } = useAuth()
  const { data: profiles = [] } = useProfiles()
  const updateProfile = useUpdateProfile()

  const currentProfile = profiles.find((p) => p.id === user?.id)

  const [displayName, setDisplayName] = useState("")
  const [originalName, setOriginalName] = useState("")
  const [loggingOut, setLoggingOut] = useState(false)
  const [themeOpen, setThemeOpen] = useState(false)
  const [weeklyViewOpen, setWeeklyViewOpen] = useState(false)
  const { theme, setTheme } = useTheme()
  const [weeklyViewStyle, setWeeklyViewStyle] = useWeeklyViewStyle()
  const [scopeOpen, setScopeOpen] = useState(false)
  const { scope, setScope } = useScope()

  useEffect(() => {
    if (currentProfile) {
      setDisplayName(currentProfile.display_name)
      setOriginalName(currentProfile.display_name)
    }
  }, [currentProfile])

  const email = user?.email || ""
  const avatarUrl = currentProfile?.avatar_url ?? null
  const loading = !currentProfile && profiles.length === 0

  const handleSave = () => {
    const payload = validateAndToast(updateProfileSchema, {
      userId: user?.id ?? '',
      displayName,
    })
    if (!payload) return
    updateProfile.mutate(payload, {
      onSuccess: () => setOriginalName(payload.displayName),
    })
  }

  const hasChanges = displayName.trim() !== originalName

  const handleLogout = async () => {
    setLoggingOut(true)
    try {
      await supabase.auth.signOut()
      window.location.href = "/login"
    } catch (e) {
      console.error('ログアウトに失敗:', e)
      toast.error('ログアウトに失敗しました')
      setLoggingOut(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-background">
        <div className="mx-auto max-w-lg px-4 py-6">
          <div className="mb-6 flex items-center gap-3">
            <Link to="/settings" replace>
              <Button variant="ghost" size="icon">
                <ArrowLeft className="size-5" />
              </Button>
            </Link>
            <h1 className="text-xl font-semibold">アカウント</h1>
          </div>
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-20" />
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-center">
                <Skeleton className="size-20 rounded-full" />
              </div>
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-48" />
              </div>
              <Separator />
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-9 w-full" />
              </div>
              <Skeleton className="h-9 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-background">
      <div className="mx-auto max-w-lg px-4 py-6">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <Link to="/settings" replace>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="size-5" />
            </Button>
          </Link>
          <h1 className="text-xl font-semibold">アカウント</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">プロフィール</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Avatar */}
            <div className="flex justify-center">
              <Avatar className="size-20">
                {avatarUrl && (
                  <AvatarImage src={avatarUrl} alt={displayName} />
                )}
                <AvatarFallback className="text-2xl">
                  {displayName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>

            {/* Email (read-only) */}
            <div className="space-y-1.5">
              <Label className="text-sm text-muted-foreground">
                メールアドレス
              </Label>
              <p className="text-sm">{email}</p>
            </div>

            <Separator />

            {/* Display name */}
            <div className="space-y-1.5">
              <Label htmlFor="display-name">表示名</Label>
              <Input
                id="display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="表示名を入力"
                autoCapitalize="off"
                enterKeyHint="done"
              />
            </div>

            {/* Save button */}
            <Button
              className="w-full"
              onClick={handleSave}
              disabled={updateProfile.isPending || !hasChanges || !displayName.trim()}
            >
              {updateProfile.isPending ? "保存中..." : "保存"}
            </Button>
          </CardContent>
        </Card>

        {/* Theme */}
        <Card className="mt-6 py-0 transition-colors hover:bg-accent">
          <CardContent className="p-0">
            <button
              onClick={() => setThemeOpen((v) => !v)}
              className="flex w-full items-center gap-4 px-4 py-4"
            >
              {(() => {
                const current = themeOptions.find((o) => o.value === theme) ?? themeOptions[2]
                const Icon = current.icon
                return <Icon className="size-5 text-muted-foreground" />
              })()}
              <span className="flex-1 text-left text-sm font-medium">テーマ</span>
              <span className="text-xs text-muted-foreground">
                {(themeOptions.find((o) => o.value === theme) ?? themeOptions[2]).label}
              </span>
              {themeOpen ? (
                <ChevronDown className="size-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="size-4 text-muted-foreground" />
              )}
            </button>
            {themeOpen && (
              <div className="flex gap-2 px-3 pb-3">
                {themeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setTheme(opt.value)}
                    className={`flex flex-1 flex-col items-center gap-1.5 rounded-md py-2.5 text-xs transition-colors ${
                      theme === opt.value
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    }`}
                  >
                    <opt.icon className="size-4" />
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Weekly View Style */}
        <Card className="mt-3 py-0 transition-colors hover:bg-accent">
          <CardContent className="p-0">
            <button
              onClick={() => setWeeklyViewOpen((v) => !v)}
              className="flex w-full items-center gap-4 px-4 py-4"
            >
              {(() => {
                const current = weeklyViewOptions.find((o) => o.value === weeklyViewStyle) ?? weeklyViewOptions[0]
                const Icon = current.icon
                return <Icon className="size-5 text-muted-foreground" />
              })()}
              <span className="flex-1 text-left text-sm font-medium">カレンダーのスタイル</span>
              <span className="text-xs text-muted-foreground">
                {(weeklyViewOptions.find((o) => o.value === weeklyViewStyle) ?? weeklyViewOptions[0]).label}
              </span>
              {weeklyViewOpen ? (
                <ChevronDown className="size-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="size-4 text-muted-foreground" />
              )}
            </button>
            {weeklyViewOpen && (
              <div className="space-y-3 px-3 pb-3">
                <div className="flex gap-2">
                  {weeklyViewOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setWeeklyViewStyle(opt.value)}
                      className={`flex flex-1 flex-col items-center gap-1.5 rounded-md py-2.5 text-xs transition-colors ${
                        weeklyViewStyle === opt.value
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-accent hover:text-foreground"
                      }`}
                    >
                      <opt.icon className="size-4" />
                      {opt.label}
                    </button>
                  ))}
                </div>
                {/* Preview */}
                <WeeklyViewPreview style={weeklyViewStyle} />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Scope Preference */}
        <Card className="mt-3 py-0 transition-colors hover:bg-accent">
          <CardContent className="p-0">
            <button
              onClick={() => setScopeOpen((v) => !v)}
              className="flex w-full items-center gap-4 px-4 py-4"
            >
              {(() => {
                const current = scopeOptions.find((o) => o.value === scope) ?? scopeOptions[0]
                const Icon = current.icon
                return <Icon className="size-5 text-muted-foreground" />
              })()}
              <span className="flex-1 text-left text-sm font-medium">集計範囲</span>
              <span className="text-xs text-muted-foreground">
                {(scopeOptions.find((o) => o.value === scope) ?? scopeOptions[0]).label}
              </span>
              {scopeOpen ? (
                <ChevronDown className="size-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="size-4 text-muted-foreground" />
              )}
            </button>
            {scopeOpen && (
              <div className="flex gap-2 px-3 pb-3">
                {scopeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setScope(opt.value as 'all' | 'household' | 'personal')}
                    className={`flex flex-1 flex-col items-center gap-1.5 rounded-md py-2.5 text-xs transition-colors ${
                      scope === opt.value
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    }`}
                  >
                    <opt.icon className="size-4" />
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-10">
          <Button
            variant="outline"
            className="w-full text-destructive hover:text-destructive"
            onClick={handleLogout}
            disabled={loggingOut}
          >
            <LogOut className="size-4" />
            {loggingOut ? "ログアウト中..." : "ログアウト"}
          </Button>
        </div>
      </div>
    </div>
  )
}
